import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { ClientEvent, SyncState, Preset, Visibility, RoomStateEvent, RoomEvent, EventType, NotificationCountType, MatrixEventEvent } from 'matrix-js-sdk';
import { useMatrixClient } from '../context/MatrixContext';
import { useI18n } from '../context/I18nContext';
import { getLastActivityTimestamp, getLastConversationEvent } from '../utils/matrixEvents';
import { generateInitialsAvatar } from '../utils/avatar';
import { useAvatar } from '../context/AvatarContext';
import logger from '../utils/logger';
import { SUPPORTED_MESSAGE_TYPES, GEMATIK_ROOM } from '../constants';
import matrixService from '../services/matrixService';
import { getRoomDisplayName, getRoomTopic } from '../utils/gematikRoomUtils';
import { shouldCauseReorder } from './useRoomList';

const toUnixMilliseconds = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  // Matrix timestamps are already in milliseconds (13+ digits)
  // If less than 1e12, assume it's in seconds and convert to ms
  return value > 1e12 ? Math.floor(value) : Math.floor(value * 1000);
};

/**
 * Hook for managing public channels (public rooms)
 * Similar to useRoomList but specifically for public rooms
 * 
 * @returns {Object} Object containing channel list and operations
 */
export const useChannels = () => {
  const client = useMatrixClient();
  const { t } = useI18n();
  const { getRoomAvatar, getFallbackAvatars, invalidateAvatar } = useAvatar();
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [roomAvatars, setRoomAvatars] = useState({}); // roomId -> avatar descriptors array
  const [initialSyncComplete, setInitialSyncComplete] = useState(false);

  // Ref to avoid dependency cycles
  const roomAvatarsRef = useRef({});

  // Debounce timer for performance optimization (matches useRoomList pattern)
  const debounceTimerRef = useRef(null);

  // Keep ref in sync with state
  roomAvatarsRef.current = roomAvatars;

  /**
   * Get the last message in a channel using the shared getLastConversationEvent function.
   * This ensures consistent behavior with useRoomList for event handling.
   */
  const getLastMessage = useCallback((room, myUserId) => {
    return getLastConversationEvent(room, myUserId, t);
  }, [t]);

  /**
   * Get the last activity timestamp for a channel
   */
  const getLastActivity = useCallback((room) => {
    const membership = room.getMyMembership();

    // Defensive check for room initialization phase
    if (!room?.currentState) {
      return 0;
    }

    // For invited rooms, use the invite event timestamp so they appear at the top
    if (membership === 'invite') {
      const myUserId = client?.getUserId();
      if (myUserId) {
        const memberEvent = room.currentState.getStateEvents('m.room.member', myUserId);
        if (memberEvent) {
          const inviteTimestamp = toUnixMilliseconds(memberEvent.getTs());
          if (inviteTimestamp) return inviteTimestamp;
        }
      }
    }

    const timeline = room.getLiveTimeline();
    if (!timeline) {
      // No timeline - use stable room creation timestamp
      const createEvent = room.currentState.getStateEvents('m.room.create', '');
      if (createEvent) {
        return toUnixMilliseconds(createEvent.getTs()) ?? 0;
      }
      return 0;
    }
    
    const events = timeline.getEvents();

    // Get timestamp of most recent conversation activity using shared utility
    // This filters out ephemeral events (typing, receipts, presence) and
    // state-only configuration events (power_levels, encryption, etc.)
    const activityTimestamp = getLastActivityTimestamp(events);
    if (activityTimestamp) {
      return toUnixMilliseconds(activityTimestamp);
    }

    // No conversation events found - use STABLE fallback instead of getLastActiveTimestamp()
    // which can change on every sync. Use room creation time which is immutable.
    const createEvent = room.currentState.getStateEvents('m.room.create', '');
    if (createEvent) {
      return toUnixMilliseconds(createEvent.getTs()) ?? 0;
    }

    // Last resort - use getLastActiveTimestamp (unstable but better than 0)
    const fallbackTimestamp = toUnixMilliseconds(room.getLastActiveTimestamp());
    return fallbackTimestamp ?? 0;
  }, [client]);

  /**
   * Debounced update to prevent excessive re-renders from rapid Matrix events.
   * Uses 150ms delay matching useRoomList for UI responsiveness.
   * Pattern mirrors useRoomList.js lines 484-496 from Task 16.
   */
  const debouncedUpdateChannels = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setChannels(prevChannels => [...prevChannels]);
      debounceTimerRef.current = null;
    }, 150);
  }, []);

  /**
   * Handle event decryption to update channel list when encrypted messages are decrypted.
   * Without this listener, encrypted messages show <encrypted> placeholder even after
   * successful decryption. This ensures the channel list preview updates to show
   * the actual decrypted message content once decryption completes.
   *
   * OPTIMIZATION: Only processes decryption events for non-DM rooms (channels) we're actually tracking.
   * This prevents unnecessary re-renders when encrypted messages in DMs are decrypted.
   */
  const handleEventDecrypted = useCallback((event) => {
    const roomId = event.getRoomId();
    if (!roomId) return;

    // Get room object to check filters
    const room = client?.getRoom(roomId);
    if (!room) return;

    // Apply same filtering logic as updateChannels()
    const membership = room.getMyMembership();
    if (membership !== 'join' && membership !== 'invite') return;

    // Only update if this is a non-DM room (channel) we're tracking
    if (matrixService.isDMRoom(roomId)) return;

    logger.debug(`useChannels: Event decrypted in tracked channel ${roomId}, updating channel list`);

    // Use functional setState to ensure we're checking current state
    setChannels(prevChannels => {
      // Verify channel is actually in our tracked list
      const isTracked = prevChannels.some(c => c.roomId === roomId);
      if (!isTracked) {
        // Channel passed filters but not in state yet - return unchanged
        // (Will be added via ClientEvent.Room or next sync)
        return prevChannels;
      }

      // Channel is tracked - trigger re-render for memo recalculation
      // This triggers the channelList memo to recalculate with fresh decrypted content
      return [...prevChannels];
    });
  }, [client]);

  /**
   * Handle timeline events to determine if channel list should be updated.
   * Filters for channel-specific events and uses debouncing for performance.
   * Mirrors useRoomList.handleTimelineEvent pattern from Task 16.
   */
  const handleTimelineEvent = useCallback((event, room, toStartOfTimeline) => {
    // Ignore backfill events (old messages being loaded)
    if (toStartOfTimeline) return;

    // Check if this room should be in our filtered list
    const membership = room.getMyMembership();
    if (membership !== 'join' && membership !== 'invite') return;

    // Filter must match updateChannels() logic - only include non-DM rooms (channels)
    if (matrixService.isDMRoom(room.roomId)) return;

    // Check if this event should trigger channel reordering
    // Skip profile-only changes (avatar, displayname) to prevent unnecessary reordering
    if (!shouldCauseReorder(event)) {
      return;
    }

    logger.debug(`useChannels: Timeline event in channel ${room.roomId}, updating channel list`);

    // Use functional setState to access current state
    setChannels(prevChannels => {
      const isTracked = prevChannels.some(c => c.roomId === room.roomId);

      if (!isTracked) {
        // Channel not in list - will be added via ClientEvent.Room
        return prevChannels;
      }

      // Channel exists - trigger debounced update for memo recalculation
      debouncedUpdateChannels();
      return prevChannels;
    });
  }, [client, debouncedUpdateChannels]);

  /**
   * Handle receipt events (read receipts) to update unread badge counts.
   * Mirrors useRoomList.handleReceiptEvent pattern from Task 16 for consistency.
   */
  const handleReceiptEvent = useCallback((event, room) => {
    if (!room) return;

    const membership = room.getMyMembership();
    if (membership !== 'join' && membership !== 'invite') return;

    // Only update channels (non-DM rooms)
    if (matrixService.isDMRoom(room.roomId)) return;

    logger.debug(`useChannels: Receipt event in channel ${room.roomId}, updating channel list`);

    setChannels(prevChannels => {
      const isTracked = prevChannels.some(c => c.roomId === room.roomId);

      if (!isTracked) {
        return prevChannels;
      }

      // Trigger debounced update
      debouncedUpdateChannels();
      return prevChannels;
    });
  }, [debouncedUpdateChannels]);

  /**
   * Get member count for display
   */
  const getMemberCount = useCallback((room) => {
    return room.getJoinedMemberCount();
  }, []);

  /**
   * Create a new public channel
   */
  const createChannel = useCallback(async (channelName, topic = '', inviteUsers = [], isPublic = false) => {
    if (!client) {
      throw new Error('Matrix client is not available to create a channel');
    }

    // Validate channelName - allow empty/undefined for auto-generated names
    if (channelName !== null && channelName !== undefined && typeof channelName !== 'string') {
      throw new Error('Channel name must be a string if provided');
    }

    // Validate topic parameter type
    if (topic !== null && topic !== undefined && typeof topic !== 'string') {
      throw new Error('Topic must be a string if provided');
    }

    const roomName = channelName?.trim() || '';
    const trimmedTopic = topic?.trim() || '';

    try {
      const roomOptions = {
        invite: inviteUsers,
        visibility: isPublic ? Visibility.Public : Visibility.Private,
        preset: isPublic ? Preset.PublicChat : Preset.PrivateChat,
        creation_content: {
          type: GEMATIK_ROOM.ROOM_TYPE,
        },
        initial_state: [
          {
            type: GEMATIK_ROOM.ROOM_DEFAULT,
            content: {},
            state_key: '',
          },
        ],
      };

      // Enable encryption for private channels
      if (!isPublic) {
        roomOptions.initial_state.push({
          type: EventType.RoomEncryption,
          state_key: '',
          content: { algorithm: 'm.megolm.v1.aes-sha2' },
        });
        logger.debug('Encryption enabled for private channel');
      }
      // Add the room name as a separate state event if provided
      if (roomName) {
        roomOptions.name = roomName;
        roomOptions.initial_state.push({
          type: GEMATIK_ROOM.ROOM_NAME,
          content: {
            name: roomName,
          },
          state_key: '',
        });
      }

      if(trimmedTopic) {
        roomOptions.topic = trimmedTopic;
        roomOptions.initial_state.push({
          type: GEMATIK_ROOM.ROOM_TOPIC,
          content: {
            topic: trimmedTopic,
          },
          state_key: '',
        });
      }

      logger.info(`Creating ${isPublic ? 'public' : 'private'} channel:`, roomName || '(no name)');
      const { room_id: roomId } = await client.createRoom(roomOptions);

      // Wait for the next sync to ensure the channel appears
      await new Promise(resolve => {
        const onSync = (state) => {
          if (state === SyncState.Syncing) {
            client.off(ClientEvent.Sync, onSync);
            resolve();
          }
        };
        client.on(ClientEvent.Sync, onSync);
      });

      logger.info('Channel created successfully:', roomId);
      return roomId;
    } catch (error) {
      logger.error('Failed to create channel:', error);
      throw error;
    }
  }, [client]);

  /**
   * Check if the current user is a channel owner/admin
   */
  const isChannelOwner = useCallback((channelId) => {
    if (!client || !channelId) return false;

    const room = client.getRoom(channelId);
    if (!room?.currentState) return false;

    const userId = client.getUserId();

    // Check if user has power level >= 50 (moderator) or >= 100 (admin)
    const powerLevel = room.currentState.getStateEvents('m.room.power_levels', '')?.getContent();
    const userPowerLevel = powerLevel?.users?.[userId] || powerLevel?.users_default || 0;
    
    return userPowerLevel >= 50;
  }, [client]);

  /**
   * Effect to load and monitor channels
   */
  useEffect(() => {
    if (!client) {
      setIsLoading(true);
      setChannels([]);
      return;
    }

    const updateChannels = () => {
      // Filter rooms to include all non-DM rooms (public channels + private group chats)
      // Excludes only rooms in m.direct account data
      const nonDMRooms = client.getRooms().filter(room => {
        const membership = room.getMyMembership();
        
        // Include both joined and invited rooms
        if (membership !== 'join' && membership !== 'invite') {
          return false;
        }

        // Include all rooms NOT in m.direct
        // This includes: public channels, private group chats, and any non-DM rooms
        return !matrixService.isDMRoom(room.roomId);
      });
      
      setChannels(nonDMRooms);
    };

    const handleSync = (state) => {
      if (state === SyncState.Prepared || state === SyncState.Syncing) {
        updateChannels();
        setIsLoading(false);
      }
      
      // Mark initial sync as complete when we reach Syncing state
      // This enables avatar invalidation for LIVE changes only
      if (state === SyncState.Syncing && !initialSyncComplete) {
        logger.debug('[useChannels] Initial sync complete - enabling avatar invalidation');
        setInitialSyncComplete(true);
      }
    };

    const handleRoom = () => {
      updateChannels();
    };

    /**
     * Handle account data changes, specifically m.direct updates.
     * This is critical because ClientEvent.Sync does NOT fire for every sync response
     * once the client is in Syncing state. When another client updates m.direct,
     * we receive a ClientEvent.AccountData event immediately, allowing us to
     * re-evaluate room grouping without waiting for state transitions.
     */
    const handleAccountData = (event) => {
      if (event.getType() === 'm.direct') {
        logger.debug('m.direct account data changed, updating channel list');
        updateChannels();
      }
    };

    // Initial load
    const syncState = client.getSyncState();
    if (syncState === SyncState.Prepared || syncState === SyncState.Syncing) {
      updateChannels();
      setIsLoading(false);
      
      // If already syncing when component mounts, mark initial sync as complete
      if (syncState === SyncState.Syncing) {
        logger.debug('[useChannels] Component mounted with sync already complete - enabling avatar invalidation');
        setInitialSyncComplete(true);
      }
    }

    // Set up listeners
    client.on(ClientEvent.Sync, handleSync);
    client.on(ClientEvent.Room, handleRoom);
    client.on(ClientEvent.AccountData, handleAccountData); // For m.direct changes
    client.on(MatrixEventEvent.Decrypted, handleEventDecrypted); // For encrypted message decryption
    client.on(RoomEvent.Timeline, handleTimelineEvent); // For timeline message updates
    client.on(RoomEvent.Receipt, handleReceiptEvent); // For read receipt updates

    return () => {
      // Cleanup debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      client.off(ClientEvent.Sync, handleSync);
      client.off(ClientEvent.Room, handleRoom);
      client.off(ClientEvent.AccountData, handleAccountData);
      client.off(MatrixEventEvent.Decrypted, handleEventDecrypted);
      client.off(RoomEvent.Timeline, handleTimelineEvent);
      client.off(RoomEvent.Receipt, handleReceiptEvent);
    };
  }, [client, handleEventDecrypted, handleTimelineEvent, handleReceiptEvent]);

  /**
   * Fetch room avatars for channels using avatarManager
   * 
   * avatarManager now supports both user and room avatars using the same
   * caching infrastructure, reference counting, and IndexedDB storage.
   */
  useEffect(() => {
    if (!client || !channels.length) {
      return;
    }

    let isMounted = true; // Guard against state updates after unmount

    const fetchRoomAvatars = async () => {
      // Filter out rooms whose avatars we already have to avoid redundant fetches
      const roomsNeedingAvatars = channels.filter(
        room => !roomAvatarsRef.current[room.roomId],
      );

      if (roomsNeedingAvatars.length === 0) {
        return; // All avatars already loaded
      }

      // Fetch all room avatars in parallel via context
      const results = await Promise.all(
        roomsNeedingAvatars.map(async (room) => {
          try {
            // Try explicit room avatar first
            const explicitAvatar = await getRoomAvatar(
              room.roomId,
              room.name || 'Channel',
              40,
            );

            if (explicitAvatar) {
              // Room has explicit avatar
              return { roomId: room.roomId, avatars: [{ url: explicitAvatar, type: 'room' }] };
            } else {
              // No explicit avatar - get fallback member avatars
              const fallbackAvatars = await getFallbackAvatars(room.roomId, 96);
              return { 
                roomId: room.roomId, 
                avatars: fallbackAvatars.map(desc => ({
                  url: desc.url,
                  type: 'user',
                })),
              };
            }
          } catch (error) {
            logger.warn(`Failed to fetch avatars for channel ${room.roomId}:`, error);
            return { roomId: room.roomId, avatars: [] };
          }
        }),
      );

      // Batch update room avatars state only if component is still mounted
      if (isMounted) {
        const newAvatars = {};
        results.forEach(result => {
          if (result && result.avatars) {
            newAvatars[result.roomId] = result.avatars;
          }
        });

        if (Object.keys(newAvatars).length > 0) {
          setRoomAvatars(prev => ({ ...prev, ...newAvatars }));
        }
      }
    };

    fetchRoomAvatars();

    // Cleanup: only prevent state updates after unmount
    return () => {
      isMounted = false;
      // No cleanup needed - AvatarContext handles it automatically
    };
  }, [client, channels, getRoomAvatar]);

  /**
   * Listen for room avatar changes and update in real-time
   */
  useEffect(() => {
    if (!client) {
      return;
    }

    const handleRoomAvatarChange = async (event) => {
      // Filter for room avatar events only
      if (event.getType() !== EventType.RoomAvatar) return;

      const changedRoomId = event.getRoomId();

      // Check if this is one of our channels
      const isChannel = channels.some(channel => channel.roomId === changedRoomId);
      if (!isChannel) {
        return; // Not a channel we're tracking
      }

      logger.debug(`useChannels: Room avatar changed for ${changedRoomId}`);

      try {
        const room = client.getRoom(changedRoomId);
        if (!room) {
          logger.warn(`useChannels: Room ${changedRoomId} not found`);
          return;
        }

        // Invalidate cache via context
        await invalidateAvatar(changedRoomId);

        // Try explicit room avatar first
        const explicitAvatar = await getRoomAvatar(
          changedRoomId,
          room.name || 'Channel',
          40,
        );

        let avatarDescriptors;
        if (explicitAvatar) {
          // Room has explicit avatar
          avatarDescriptors = [{ url: explicitAvatar, type: 'room' }];
        } else {
          // No explicit avatar - get fallback member avatars
          const fallbackAvatars = await getFallbackAvatars(changedRoomId, 96);
          avatarDescriptors = fallbackAvatars.map(desc => ({
            url: desc.url,
            type: 'user',
          }));
        }

        // Update state with new avatars
        setRoomAvatars(prev => ({
          ...prev,
          [changedRoomId]: avatarDescriptors,
        }));

        logger.debug(`useChannels: Room avatar updated for ${changedRoomId}`);
      } catch (error) {
        logger.warn(`useChannels: Error updating room avatar for ${changedRoomId}:`, error);
        // On error, set empty array to fall back to initials
        setRoomAvatars(prev => ({
          ...prev,
          [changedRoomId]: [],
        }));
      }
    };

    client.on(RoomStateEvent.Events, handleRoomAvatarChange);

    logger.debug('useChannels: Room avatar change listener registered');

    return () => {
      client.off(RoomStateEvent.Events, handleRoomAvatarChange);
      logger.debug('useChannels: Room avatar change listener removed');
    };
  }, [client, channels, invalidateAvatar, getRoomAvatar, getFallbackAvatars]);

  /**
   * Listen for member profile changes (avatar updates) in channels
   * This handles when a user changes their avatar and we need to update
   * the conversation list to reflect the new avatar.
   * 
   * CRITICAL: Uses initialSyncComplete flag to skip historical avatar changes
   * during initial sync, preventing race conditions where avatars get invalidated
   * before they finish caching.
   */
  useEffect(() => {
    if (!client) {
      return;
    }

    const handleMemberProfileChange = async (event, room, toStartOfTimeline) => {
      // Ignore backfill events
      if (toStartOfTimeline) return;
      
      // Only process m.room.member events
      if (event.getType() !== 'm.room.member') return;

      const content = event.getContent();
      const prevContent = event.getPrevContent();

      // Check if avatar_url changed
      if (content.avatar_url !== prevContent?.avatar_url) {
        const changedUserId = event.getStateKey();

        // CRITICAL FIX: Skip invalidation during initial sync
        // During initial sync, Matrix replays historical member events (including avatar changes).
        // We don't need to invalidate anything because:
        // 1. Nothing is cached yet - channel list will fetch current avatar state directly
        // 2. Historical intermediate states don't matter - only final state matters
        // 3. This prevents race condition where fresh avatars are invalidated immediately after caching
        // After initial sync completes, we process LIVE avatar changes normally.
        if (!initialSyncComplete) {
          logger.debug('[useChannels] Skipping avatar update during initial sync', {
            userId: changedUserId,
            roomId: room?.roomId,
          });
          return;
        }

        logger.debug('[useChannels] Member avatar URL changed', {
          userId: changedUserId,
          roomId: room?.roomId,
          oldUrl: prevContent?.avatar_url,
          newUrl: content.avatar_url,
        });

        // Check if this is one of our channels
        const isChannel = channels.some(channel => channel.roomId === room?.roomId);
        if (!isChannel) {
          logger.debug('[useChannels] Room not in channels list, skipping');
          return;
        }

        try {
          // Invalidate the user's avatar cache
          await invalidateAvatar(changedUserId);
          logger.debug('[useChannels] Invalidated avatar cache for user', { userId: changedUserId });

          // For channels with explicit room avatar, no need to update
          // (member avatars only used for fallback)
          const roomObj = client.getRoom(room.roomId);
          if (!roomObj) return;

          const explicitAvatar = await getRoomAvatar(
            room.roomId,
            roomObj.name || 'Channel',
            40,
          );

          if (explicitAvatar) {
            logger.debug('[useChannels] Room has explicit avatar, no fallback update needed');
            return; // Room has its own avatar, don't update
          }

          // Re-fetch fallback avatars since a member's avatar changed
          const fallbackAvatars = await getFallbackAvatars(room.roomId, 96);
          const avatarDescriptors = fallbackAvatars.map(desc => ({
            url: desc.url,
            type: 'user',
          }));

          // Update state with new avatars
          setRoomAvatars(prev => ({
            ...prev,
            [room.roomId]: avatarDescriptors,
          }));

          logger.debug('[useChannels] Updated fallback avatars for room', {
            roomId: room.roomId,
            avatarCount: avatarDescriptors.length,
          });
        } catch (error) {
          logger.warn('[useChannels] Error updating member avatar:', error);
        }
      }
    };

    client.on(RoomEvent.Timeline, handleMemberProfileChange);

    logger.debug('[useChannels] Member profile change listener registered');

    return () => {
      client.off(RoomEvent.Timeline, handleMemberProfileChange);
      logger.debug('[useChannels] Member profile change listener removed');
    };
  }, [client, channels, getRoomAvatar, getFallbackAvatars, invalidateAvatar, initialSyncComplete]);

  /**
   * Process channels into display format
   */
  const channelList = useMemo(() => {
    if (isLoading || !channels.length) {
      return [];
    }

    const myUserId = client?.getUserId();

    return channels.map(room => {
      const lastActivity = getLastActivity(room);
      const membership = room.getMyMembership();

      // Get channel name considering Gematik custom name
      const channelName = getRoomDisplayName(room);
      const topic = getRoomTopic(room);

      // Extract avatar URLs from descriptors, filtering out any undefined/null entries
      const descriptors = roomAvatars[room.roomId];
      let avatarUrls = [];
      
      if (Array.isArray(descriptors)) {
        avatarUrls = descriptors
          .filter(descriptor => {
            // Ensure descriptor is an object with a url property
            if (!descriptor || typeof descriptor !== 'object') {
              return false;
            }
            return descriptor.url && typeof descriptor.url === 'string';
          })
          .map(descriptor => descriptor.url);
      }

      // Fallback to initials if no avatars
      if (avatarUrls.length === 0) {
        avatarUrls = [generateInitialsAvatar(channelName || 'CH')];
      }

      const unreadCount = room.getUnreadNotificationCount(NotificationCountType.Total);
      const highlightCount = room.getUnreadNotificationCount(NotificationCountType.Highlight);

      // Check if the channel is encrypted by looking for m.room.encryption state event
      const encryptionEvent = room.currentState.getStateEvents('m.room.encryption', '');
      const isEncrypted = !!encryptionEvent;

      return {
        id: room.roomId,
        name: channelName || 'Unnamed Channel',
        title: channelName || 'Unnamed Channel', // Add title for SideBar compatibility
        topic,
        lastMessage: getLastMessage(room, myUserId),
        lastActivity,
        memberCount: getMemberCount(room),
        avatar: avatarUrls[0], // Keep single avatar for other uses
        avatars: avatarUrls, // Provide full array for ConversationList
        isOwner: isChannelOwner(room.roomId),
        unreadCount,
        isInvite: membership === 'invite',
        isEncrypted,
      };
    });
  }, [client, channels, isLoading, getLastMessage, getLastActivity, getMemberCount, isChannelOwner, roomAvatars]);

  return {
    channelList,
    isLoading,
    createChannel,
    isChannelOwner,
  };
};

export default useChannels;
